import { Link } from '@tanstack/react-router'
import { AlertCircle, Home, RotateCcw } from 'lucide-react'
import type { ErrorComponentProps } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Header } from '@/components/Header'
import { Footer } from '@/components/Footer'

/** Root-level errorComponent: there is no shared layout route, so — like
 * AppNotFoundPage — it has to render the site Header/Footer itself. */
export function AppErrorPage({ error, reset }: ErrorComponentProps) {
  const showDevMessage = import.meta.env.DEV
  const errorMessage =
    error instanceof Error ? error.message : 'Unexpected application error'

  return (
    <div className="relative min-h-screen flex flex-col overflow-x-hidden bg-[var(--color-bg-primary)]">
      <Header />
      <main className="flex min-h-screen items-center justify-center px-4 pt-16 pb-12">
        <div className="glass-panel w-full max-w-xl rounded-3xl border border-[var(--color-error-border)] p-8 text-center space-y-6">
          {/* Error Icon */}
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--color-error-bg)]">
            <AlertCircle className="w-8 h-8 text-[var(--color-error-text)]" />
          </div>

          {/* Error Message */}
          <div className="space-y-2">
            <h1 className="text-2xl font-bold tracking-tight text-[var(--color-text-primary)]">
              Something went wrong
            </h1>
            <p className="text-sm text-[var(--color-text-secondary)]">
              We hit an unexpected issue. Please try again or go back to the home
              page.
            </p>
          </div>

          {/* Dev Error Details */}
          {showDevMessage && (
            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-secondary)] p-4 text-left space-y-1">
              <p className="text-xs font-medium text-[var(--color-text-secondary)]">
                Error Details (Development Only)
              </p>
              <p className="text-xs text-[var(--color-text-tertiary)] break-words font-mono">
                {errorMessage}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
            <Button variant="primary" onClick={() => reset()}>
              <RotateCcw className="w-4 h-4" />
              Retry
            </Button>

            <Button variant="secondary" asChild>
              <Link to="/">
                <Home className="w-4 h-4" />
                Go Home
              </Link>
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}
